import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  InputAdornment,
  Chip,
  Stack,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  FormGroup,
  Checkbox,
  Divider,
  Collapse,
} from '@mui/material';
import {
  getPartnerDetail,
  updatePartnerAdmin,
  deactivatePartner,
  reactivatePartner,
  getPartnerHistory,
  getPartnerUsers,
  addPartnerUser,
  removePartnerUser,
  updatePartnerUserRoles,
  getPartnerSettings,
  updatePartnerSettings,
  getPartnerActivity,
  exportPartnerActivity,
  createPartnersApi,
  ApiClient,
} from '../services/api';
import { env } from '../config/env';
import type {
  PartnerDetailResponse,
  PartnerRecord,
  PortalUser,
  PartnerSettings,
  ChangeHistoryEntry,
  ActivityLogEntry,
  InstitutionType,
  PartnerRole,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { usePartnerScope } from '../contexts/PartnerScopeContext';
import { ImpersonationReadOnlyBanner } from '../components/ImpersonationReadOnlyBanner';

const INSTITUTION_TYPE_LABELS: Record<InstitutionType, string> = {
  university: 'University',
  college: 'College',
  community_college: 'Community College',
  technical_school: 'Technical School',
};

const ROLE_LABELS: Record<PartnerRole, string> = {
  sophia_admin: 'Admin',
  transcript_viewer: 'Transcript Viewer',
  course_mapper: 'Course Mapper',
  api_developer: 'API Developer',
  academic_advisor: 'Academic Advisor',
  bucket_management: 'Bucket Management',
};

const ALL_ROLES: PartnerRole[] = [
  'sophia_admin',
  'transcript_viewer',
  'course_mapper',
  'api_developer',
  'academic_advisor',
  'bucket_management',
];

const SUB_TAB_WIDTH = 120;

// Traditional form styling — 4px radius for data-entry forms (not filters)
const formSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '4px' },
  '& .MuiInputBase-root': { borderRadius: '4px' },
};

const formatDate = (dateStr: string | undefined | null) => {
  if (!dateStr) return '--';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatDateTime = (dateStr: string) => {
  return new Date(dateStr).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ─── Details Tab ─────────────────────────────────────────────────────

function DetailsTab({
  partner,
  onUpdate,
  client,
  isAdmin,
}: {
  partner: PartnerRecord;
  onUpdate: (updated: PartnerRecord) => void;
  client: import('../services/api').ApiClient;
  isAdmin: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    institution_name: partner.institution_name,
    email_domain: partner.email_domain,
    primary_contact_email: partner.primary_contact_email,
    institution_type: partner.institution_type,
    mailing_address: partner.mailing_address || '',
    phone: partner.phone || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Change history
  const [history, setHistory] = useState<ChangeHistoryEntry[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    getPartnerHistory(partner.partner_id, client)
      .then((res) => setHistory(res.items))
      .catch((err) => console.error('[PartnerDetail] History fetch failed:', err));
  }, [partner.partner_id, client]);

  const handleEdit = () => {
    setForm({
      institution_name: partner.institution_name,
      email_domain: partner.email_domain,
      primary_contact_email: partner.primary_contact_email,
      institution_type: partner.institution_type,
      mailing_address: partner.mailing_address || '',
      phone: partner.phone || '',
    });
    setEditing(true);
    setError(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const updated = await updatePartnerAdmin(
        partner.partner_id,
        {
          institution_name: form.institution_name,
          email_domain: form.email_domain,
          primary_contact_email: form.primary_contact_email,
          institution_type: form.institution_type,
          mailing_address: form.mailing_address || undefined,
          phone: form.phone || undefined,
        },
        client
      );
      onUpdate(updated);
      setEditing(false);
      // Refresh history
      const hist = await getPartnerHistory(partner.partner_id, client);
      setHistory(hist.items);
    } catch (err) {
      console.error('[PartnerDetail] Update failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isAdmin && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          {!editing ? (
            <Button variant="outlined" size="small" onClick={handleEdit}>
              Edit
            </Button>
          ) : (
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <Button variant="contained" size="small" onClick={handleSave} disabled={saving}>
                {saving ? <CircularProgress size={18} /> : 'Save'}
              </Button>
            </Stack>
          )}
        </Box>
      )}

      <Stack spacing={2.5} sx={{ maxWidth: 600, ...formSx }}>
        <TextField
          label="Institution Name"
          fullWidth
          value={editing ? form.institution_name : partner.institution_name}
          onChange={(e) => setForm({ ...form, institution_name: e.target.value })}
          InputProps={{ readOnly: !editing }}
        />
        <TextField
          label="Email Domain"
          fullWidth
          value={editing ? form.email_domain : partner.email_domain}
          onChange={(e) => setForm({ ...form, email_domain: e.target.value })}
          InputProps={{ readOnly: !editing }}
        />
        <TextField
          label="Primary Contact Email"
          fullWidth
          value={editing ? form.primary_contact_email : partner.primary_contact_email}
          onChange={(e) => setForm({ ...form, primary_contact_email: e.target.value })}
          InputProps={{ readOnly: !editing }}
        />
        {editing ? (
          <FormControl fullWidth>
            <InputLabel>Institution Type</InputLabel>
            <Select
              label="Institution Type"
              value={form.institution_type}
              onChange={(e) =>
                setForm({ ...form, institution_type: e.target.value as InstitutionType })
              }
            >
              <MenuItem value="university">University</MenuItem>
              <MenuItem value="college">College</MenuItem>
              <MenuItem value="community_college">Community College</MenuItem>
              <MenuItem value="technical_school">Technical School</MenuItem>
            </Select>
          </FormControl>
        ) : (
          <TextField
            label="Institution Type"
            fullWidth
            value={INSTITUTION_TYPE_LABELS[partner.institution_type] || partner.institution_type}
            InputProps={{ readOnly: true }}
          />
        )}
        <TextField
          label="Mailing Address"
          fullWidth
          value={editing ? form.mailing_address : partner.mailing_address || ''}
          onChange={(e) => setForm({ ...form, mailing_address: e.target.value })}
          InputProps={{ readOnly: !editing }}
        />
        <TextField
          label="Phone"
          fullWidth
          value={editing ? form.phone : partner.phone || ''}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          InputProps={{ readOnly: !editing }}
        />
      </Stack>

      {/* Change History */}
      <Divider sx={{ my: 4 }} />
      <Box
        onClick={() => setHistoryOpen(!historyOpen)}
        sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
      >
        <i
          className={historyOpen ? 'fa-thin fa-chevron-down' : 'fa-thin fa-chevron-right'}
          style={{ fontSize: 14 }}
        />
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Change History ({history.length})
        </Typography>
      </Box>
      <Collapse in={historyOpen}>
        {history.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No changes recorded yet.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Field</TableCell>
                  <TableCell>Old Value</TableCell>
                  <TableCell>New Value</TableCell>
                  <TableCell>Changed By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell>{formatDateTime(h.changed_at)}</TableCell>
                    <TableCell>{h.field}</TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {h.old_value || '--'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {h.new_value}
                    </TableCell>
                    <TableCell>{h.changed_by}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Collapse>
    </Box>
  );
}

// ─── Users Tab ───────────────────────────────────────────────────────

function UsersTab({
  partnerId,
  client,
  isAdmin,
}: {
  partnerId: string;
  client: import('../services/api').ApiClient;
  isAdmin: boolean;
}) {
  const [users, setUsers] = useState<PortalUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  // Add user dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    email: '',
    display_name: '',
    roles: ['transcript_viewer'] as PartnerRole[],
  });
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  // Edit roles dialog
  const [editUser, setEditUser] = useState<PortalUser | null>(null);
  const [editRoles, setEditRoles] = useState<PartnerRole[]>([]);
  const [editSaving, setEditSaving] = useState(false);

  // Remove confirm
  const [removeUser, setRemoveUser] = useState<PortalUser | null>(null);
  const [removing, setRemoving] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPartnerUsers(
        partnerId,
        {
          search: search || undefined,
          role: roleFilter || undefined,
        },
        client
      );
      setUsers(data.items);
    } catch (err) {
      console.error('[PartnerDetail/Users] Fetch failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [partnerId, search, roleFilter, client]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleAdd = async () => {
    if (!addForm.email || !addForm.display_name) {
      setAddError('Email and display name are required.');
      return;
    }
    try {
      setAdding(true);
      setAddError(null);
      await addPartnerUser(
        partnerId,
        {
          email: addForm.email,
          display_name: addForm.display_name,
          roles: addForm.roles.length > 0 ? addForm.roles : undefined,
        },
        client
      );
      setAddOpen(false);
      setAddForm({ email: '', display_name: '', roles: ['transcript_viewer'] });
      fetchUsers();
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to add user');
    } finally {
      setAdding(false);
    }
  };

  const handleEditRoles = async () => {
    if (!editUser || editRoles.length === 0) return;
    try {
      setEditSaving(true);
      await updatePartnerUserRoles(partnerId, editUser.user_id, editRoles, client);
      setEditUser(null);
      fetchUsers();
    } catch (err) {
      console.error('[PartnerDetail/Users] Role update failed:', err);
    } finally {
      setEditSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!removeUser) return;
    try {
      setRemoving(true);
      await removePartnerUser(partnerId, removeUser.user_id, client);
      setRemoveUser(null);
      fetchUsers();
    } catch (err) {
      console.error('[PartnerDetail/Users] Remove failed:', err);
    } finally {
      setRemoving(false);
    }
  };

  const toggleAddRole = (role: PartnerRole) => {
    setAddForm((prev) => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter((r) => r !== role)
        : [...prev.roles, role],
    }));
  };

  const toggleEditRole = (role: PartnerRole) => {
    setEditRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  return (
    <Box>
      <ImpersonationReadOnlyBanner />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <i
                  className="fa-thin fa-magnifying-glass"
                  style={{ color: 'rgba(0,0,0,0.54)', fontSize: 18 }}
                />
              </InputAdornment>
            ),
          }}
        />
        <Select
          size="small"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          displayEmpty
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All Roles</MenuItem>
          {ALL_ROLES.map((r) => (
            <MenuItem key={r} value={r}>
              {ROLE_LABELS[r]}
            </MenuItem>
          ))}
        </Select>
        <Box sx={{ flex: 1 }} />
        {isAdmin && (
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              setAddForm({ email: '', display_name: '', roles: ['transcript_viewer'] });
              setAddError(null);
              setAddOpen(true);
            }}
          >
            Add User
          </Button>
        )}
      </Stack>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Display Name</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell>Assigned</TableCell>
                {isAdmin && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 5 : 4} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 5 : 4} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">No users assigned.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((u) => (
                  <TableRow key={u.user_id} hover>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{u.display_name}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {u.roles.map((r) => (
                          <Chip
                            key={r}
                            label={ROLE_LABELS[r] || r}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>{formatDate(u.created_at)}</TableCell>
                    {isAdmin && (
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <IconButton
                            size="small"
                            title="Edit roles"
                            onClick={() => {
                              setEditUser(u);
                              setEditRoles([...u.roles]);
                            }}
                          >
                            <i className="fa-thin fa-pen" style={{ fontSize: 14 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            title="Remove user"
                            onClick={() => setRemoveUser(u)}
                            color="error"
                          >
                            <i className="fa-thin fa-trash" style={{ fontSize: 14 }} />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add User Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add User</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1, ...formSx }}>
            {addError && <Alert severity="error">{addError}</Alert>}
            <TextField
              label="Email"
              required
              fullWidth
              value={addForm.email}
              onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
            />
            <TextField
              label="Display Name"
              required
              fullWidth
              value={addForm.display_name}
              onChange={(e) => setAddForm({ ...addForm, display_name: e.target.value })}
            />
            <Typography variant="subtitle2">Roles</Typography>
            <FormGroup>
              {ALL_ROLES.map((r) => (
                <FormControlLabel
                  key={r}
                  control={
                    <Checkbox
                      checked={addForm.roles.includes(r)}
                      onChange={() => toggleAddRole(r)}
                      size="small"
                    />
                  }
                  label={ROLE_LABELS[r]}
                />
              ))}
            </FormGroup>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={adding}>
            {adding ? <CircularProgress size={20} /> : 'Add User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Roles Dialog */}
      <Dialog open={!!editUser} onClose={() => setEditUser(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Roles — {editUser?.display_name}</DialogTitle>
        <DialogContent>
          <FormGroup sx={{ mt: 1 }}>
            {ALL_ROLES.map((r) => (
              <FormControlLabel
                key={r}
                control={
                  <Checkbox
                    checked={editRoles.includes(r)}
                    onChange={() => toggleEditRole(r)}
                    size="small"
                  />
                }
                label={ROLE_LABELS[r]}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditUser(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEditRoles}
            disabled={editSaving || editRoles.length === 0}
          >
            {editSaving ? <CircularProgress size={20} /> : 'Save Roles'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Confirmation */}
      <Dialog open={!!removeUser} onClose={() => setRemoveUser(null)} maxWidth="xs">
        <DialogTitle>Remove User</DialogTitle>
        <DialogContent>
          <Typography>
            Remove <strong>{removeUser?.display_name}</strong> ({removeUser?.email}) from this
            partner?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRemoveUser(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleRemove} disabled={removing}>
            {removing ? <CircularProgress size={20} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Portal Launch Date ──────────────────────────────────────────────
// Stored in the portal backend (Firestore), not the partners API: transcripts
// certified before this date read as already reviewed on the Transcripts page
// (SOPHIA-35018). With all-time history exposed (SOPHIA-35148), a partner
// without a launch date sees their entire pre-Portal history as Awaiting
// Review — the section warns admins when the field is empty.

function PortalLaunchDateSection({ partnerId, isAdmin }: { partnerId: string; isAdmin: boolean }) {
  const { idToken } = useAuth();
  const backendClient = useMemo(() => new ApiClient(idToken, env.apiUrl), [idToken]);

  const [launchDate, setLaunchDate] = useState<string>('');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    backendClient
      .getPartnerPortalConfig(partnerId)
      .then((config) => {
        setLaunchDate(config.portal_launch_date ?? '');
        setLoaded(true);
      })
      .catch((err) => {
        console.error('[PartnerDetail/PortalLaunchDate] Fetch failed:', err);
        setError('Failed to load Portal launch date');
      });
  }, [partnerId, backendClient]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSaved(false);
      const updated = await backendClient.updatePartnerPortalConfig(partnerId, launchDate || null);
      setLaunchDate(updated.portal_launch_date ?? '');
      setSaved(true);
    } catch (err) {
      console.error('[PartnerDetail/PortalLaunchDate] Save failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to save Portal launch date');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2} sx={{ maxWidth: 600, mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        Portal Launch Date
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {saved && (
        <Alert severity="success" onClose={() => setSaved(false)}>
          Portal launch date saved.
        </Alert>
      )}
      {loaded && !launchDate && (
        <Alert severity="warning">
          No launch date is set. The Portal shows all-time transcript history, so this
          partner&apos;s entire pre-Portal history will appear as Awaiting Review until a launch
          date is saved.
        </Alert>
      )}
      <TextField
        label="Launch Date"
        type="date"
        fullWidth
        value={launchDate}
        onChange={(e) => {
          setLaunchDate(e.target.value);
          setSaved(false);
        }}
        InputLabelProps={{ shrink: true }}
        InputProps={{ readOnly: !isAdmin }}
        disabled={!loaded && !error}
        helperText="Transcripts certified before this date were sent by email and show as Reviewed in the Portal. Set this when onboarding every partner with pre-Portal transcript history — the Portal shows all-time history, so leaving it empty marks that entire history as Awaiting Review."
      />
      {isAdmin && (
        <Box>
          <Button variant="contained" onClick={handleSave} disabled={saving || !loaded}>
            {saving ? <CircularProgress size={20} /> : 'Save Launch Date'}
          </Button>
        </Box>
      )}
    </Stack>
  );
}

// ─── Settings Tab ────────────────────────────────────────────────────

function SettingsTab({
  partnerId,
  client,
  isAdmin,
  canEdit,
}: {
  partnerId: string;
  client: import('../services/api').ApiClient;
  isAdmin: boolean;
  canEdit: boolean;
}) {
  const [settings, setSettings] = useState<PartnerSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState<Partial<PartnerSettings>>({});

  useEffect(() => {
    getPartnerSettings(partnerId, client)
      .then((s) => {
        setSettings(s);
        setForm(s);
      })
      .catch((err) => {
        console.error('[PartnerDetail/Settings] Fetch failed:', err);
        setError(err instanceof Error ? err.message : 'Failed to load settings');
      })
      .finally(() => setLoading(false));
  }, [partnerId, client]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const updated = await updatePartnerSettings(
        partnerId,
        {
          transcript_bucket_location: form.transcript_bucket_location,
          api_rate_limit: form.api_rate_limit,
          session_timeout_minutes: form.session_timeout_minutes,
          mfa_required: form.mfa_required,
          sso_enabled: form.sso_enabled,
          sso_idp_metadata_url: form.sso_idp_metadata_url,
          sso_entity_id: form.sso_entity_id,
          email_notifications_enabled: form.email_notifications_enabled,
          default_notification_frequency: form.default_notification_frequency,
          default_user_roles: form.default_user_roles,
        },
        client
      );
      setSettings(updated);
      setForm(updated);
    } catch (err) {
      console.error('[PartnerDetail/Settings] Save failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!settings) {
    return (
      <Box sx={{ maxWidth: 600 }}>
        <PortalLaunchDateSection partnerId={partnerId} isAdmin={isAdmin} />
        <Divider sx={{ mb: 3 }} />
        <Alert severity="info">No settings configured for this partner.</Alert>
      </Box>
    );
  }

  const toggleDefaultRole = (role: PartnerRole) => {
    const current = form.default_user_roles || [];
    setForm({
      ...form,
      default_user_roles: current.includes(role)
        ? current.filter((r) => r !== role)
        : [...current, role],
    });
  };

  return (
    <Box sx={{ maxWidth: 600, ...formSx }}>
      <PortalLaunchDateSection partnerId={partnerId} isAdmin={isAdmin} />
      <Divider sx={{ mb: 3 }} />
      <ImpersonationReadOnlyBanner />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Storage */}
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Storage
        </Typography>
        <TextField
          label="Transcript Bucket Location"
          fullWidth
          value={form.transcript_bucket_location || ''}
          onChange={(e) => setForm({ ...form, transcript_bucket_location: e.target.value })}
          InputProps={{ readOnly: !canEdit }}
        />

        <Divider />

        {/* Rate Limiting */}
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Rate Limiting
        </Typography>
        <TextField
          label="API Rate Limit (requests/hour)"
          type="number"
          fullWidth
          value={form.api_rate_limit ?? ''}
          onChange={(e) => setForm({ ...form, api_rate_limit: parseInt(e.target.value) || 0 })}
          inputProps={{ min: 100, max: 100000 }}
          InputProps={{ readOnly: !canEdit }}
          helperText="100 – 100,000"
        />

        <Divider />

        {/* Session */}
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Session
        </Typography>
        <TextField
          label="Session Timeout (minutes)"
          type="number"
          fullWidth
          value={form.session_timeout_minutes ?? ''}
          onChange={(e) =>
            setForm({ ...form, session_timeout_minutes: parseInt(e.target.value) || 0 })
          }
          inputProps={{ min: 5, max: 480 }}
          InputProps={{ readOnly: !canEdit }}
          helperText="5 – 480 minutes"
        />

        <Divider />

        {/* Security */}
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Security
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={form.mfa_required ?? false}
              onChange={(e) => setForm({ ...form, mfa_required: e.target.checked })}
              disabled={!canEdit}
            />
          }
          label="MFA Required"
        />
        <FormControlLabel
          control={
            <Switch
              checked={form.sso_enabled ?? false}
              onChange={(e) => setForm({ ...form, sso_enabled: e.target.checked })}
              disabled={!canEdit}
            />
          }
          label="SSO Enabled"
        />
        {form.sso_enabled && (
          <>
            <TextField
              label="SSO IDP Metadata URL"
              fullWidth
              value={form.sso_idp_metadata_url || ''}
              onChange={(e) => setForm({ ...form, sso_idp_metadata_url: e.target.value })}
              InputProps={{ readOnly: !canEdit }}
            />
            <TextField
              label="SSO Entity ID"
              fullWidth
              value={form.sso_entity_id || ''}
              onChange={(e) => setForm({ ...form, sso_entity_id: e.target.value })}
              InputProps={{ readOnly: !canEdit }}
            />
          </>
        )}

        <Divider />

        {/* Notifications */}
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Notifications
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={form.email_notifications_enabled ?? false}
              onChange={(e) => setForm({ ...form, email_notifications_enabled: e.target.checked })}
              disabled={!canEdit}
            />
          }
          label="Email Notifications"
        />
        <FormControl fullWidth disabled={!canEdit}>
          <InputLabel>Default Notification Frequency</InputLabel>
          <Select
            label="Default Notification Frequency"
            value={form.default_notification_frequency || 'daily'}
            onChange={(e) =>
              setForm({
                ...form,
                default_notification_frequency: e.target
                  .value as PartnerSettings['default_notification_frequency'],
              })
            }
            readOnly={!canEdit}
          >
            <MenuItem value="immediate">Immediate</MenuItem>
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="off">Off</MenuItem>
          </Select>
        </FormControl>

        <Divider />

        {/* Default Roles */}
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Default User Roles
        </Typography>
        <FormGroup>
          {ALL_ROLES.map((r) => (
            <FormControlLabel
              key={r}
              control={
                <Checkbox
                  checked={(form.default_user_roles || []).includes(r)}
                  onChange={() => toggleDefaultRole(r)}
                  size="small"
                  disabled={!canEdit}
                />
              }
              label={ROLE_LABELS[r]}
            />
          ))}
        </FormGroup>

        {canEdit && (
          <Box sx={{ pt: 1 }}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? <CircularProgress size={20} /> : 'Save Settings'}
            </Button>
          </Box>
        )}
      </Stack>
    </Box>
  );
}

// ─── Activity Tab ────────────────────────────────────────────────────

function ActivityTab({
  partnerId,
  client,
}: {
  partnerId: string;
  client: import('../services/api').ApiClient;
}) {
  const [items, setItems] = useState<ActivityLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(100);

  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [actionType, setActionType] = useState('');
  const [exporting, setExporting] = useState(false);

  const fetchActivity = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPartnerActivity(
        partnerId,
        {
          page: page + 1,
          per_page: rowsPerPage,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          user_email: userEmail || undefined,
          action_type: actionType || undefined,
        },
        client
      );
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      console.error('[PartnerDetail/Activity] Fetch failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, [partnerId, page, rowsPerPage, dateFrom, dateTo, userEmail, actionType, client]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await exportPartnerActivity(partnerId, {
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        user_email: userEmail || undefined,
        action_type: actionType || undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity_${partnerId}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[PartnerDetail/Activity] Export failed:', err);
      setError('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ mb: 2 }}
        flexWrap="wrap"
        useFlexGap
      >
        <TextField
          size="small"
          label="From Date"
          type="date"
          value={dateFrom}
          onChange={(e) => {
            setDateFrom(e.target.value);
            setPage(0);
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        <TextField
          size="small"
          label="To Date"
          type="date"
          value={dateTo}
          onChange={(e) => {
            setDateTo(e.target.value);
            setPage(0);
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        <TextField
          size="small"
          placeholder="User email..."
          value={userEmail}
          onChange={(e) => {
            setUserEmail(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <i
                  className="fa-thin fa-user"
                  style={{ color: 'rgba(0,0,0,0.54)', fontSize: 16 }}
                />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          size="small"
          placeholder="Action type..."
          value={actionType}
          onChange={(e) => {
            setActionType(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 160 }}
        />
        <Box sx={{ flex: 1 }} />
        <Typography
          component="span"
          variant="body2"
          onClick={!exporting ? handleExport : undefined}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            color: !exporting ? 'primary.main' : 'text.disabled',
            fontWeight: 600,
            cursor: !exporting ? 'pointer' : 'default',
            whiteSpace: 'nowrap',
            '&:hover': !exporting ? { textDecoration: 'underline' } : {},
          }}
        >
          {exporting ? (
            <CircularProgress size={16} />
          ) : (
            <i className="fa-thin fa-file-arrow-down" style={{ fontSize: 18 }} />
          )}
          Export CSV
        </Typography>
      </Stack>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Details</TableCell>
                <TableCell>IP Address</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">No activity recorded.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((a) => (
                  <TableRow key={a.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(a.timestamp)}
                    </TableCell>
                    <TableCell>{a.user_email}</TableCell>
                    <TableCell>
                      <Chip label={a.action_type} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {a.details}
                    </TableCell>
                    <TableCell>{a.ip_address}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={() => {}}
          rowsPerPageOptions={[100]}
        />
      </Paper>
    </Box>
  );
}

// ─── Main PartnerDetail component ────────────────────────────────────

export function PartnerDetail(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { idToken, roles } = useAuth();
  const isAdmin = roles.includes('sophia_admin');
  // SOPHIA-35120: internal users viewing a partner get read-only Users and
  // Settings tabs (the APIs reject those writes too). Partner-lifecycle
  // controls (Details, deactivate, launch date) stay admin-editable.
  const { isInternal } = usePartnerScope();
  const canEditPartner = isAdmin && !isInternal;
  const partnersClient = useMemo(() => createPartnersApi(idToken), [idToken]);

  const [detail, setDetail] = useState<PartnerDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    if (!id) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getPartnerDetail(id, partnersClient);
        setDetail(data);
      } catch (err) {
        console.error('[PartnerDetail] Fetch failed:', err);
        setError(err instanceof Error ? err.message : 'Failed to load partner');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, partnersClient]);

  const handleToggleStatus = async () => {
    if (!id || !detail?.portal) return;
    try {
      setToggling(true);
      const updated =
        detail.portal.status === 'active'
          ? await deactivatePartner(id, partnersClient)
          : await reactivatePartner(id, partnersClient);
      setDetail({ ...detail, portal: updated });
    } catch (err) {
      console.error('[PartnerDetail] Status toggle failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setToggling(false);
    }
  };

  const handlePartnerUpdate = (updated: PartnerRecord) => {
    if (detail) {
      setDetail({ ...detail, portal: updated });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !detail || (!detail.portal && !detail.program)) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Partner not found'}</Alert>
      </Container>
    );
  }

  const partner = detail.portal;
  const program = detail.program;
  const isActive = partner ? partner.status === 'active' : (program?.active ?? false);
  const partnerName = partner?.institution_name ?? program?.name ?? 'Unknown';
  const partnerDomain = partner?.email_domain;
  const rawType = partner
    ? INSTITUTION_TYPE_LABELS[partner.institution_type] || partner.institution_type
    : (program?.partner_account_type ?? '');
  const partnerType = rawType && rawType !== 'None' ? rawType : '';

  return (
    <>
      {/* Header band */}
      <Box sx={{ bgcolor: 'background.default' }}>
        <Container maxWidth="xl" sx={{ pt: 3, pb: 3 }}>
          {isAdmin && (
            <Typography
              variant="body2"
              onClick={() => navigate('/partners')}
              sx={{
                color: 'primary.main',
                cursor: 'pointer',
                mb: 2,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              <i className="fa-thin fa-arrow-left" style={{ fontSize: 14 }} />
              Partners
            </Typography>
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h1">{partnerName}</Typography>
              <Chip
                label={isActive ? 'Active' : 'Inactive'}
                size="small"
                sx={{
                  bgcolor: isActive ? '#E8F5E9' : '#F5F5F5',
                  color: isActive ? '#2E7D32' : '#616161',
                  fontWeight: 600,
                }}
              />
            </Box>
            {isAdmin && partner && (
              <Button
                variant="outlined"
                size="small"
                color={isActive ? 'error' : 'success'}
                onClick={handleToggleStatus}
                disabled={toggling}
              >
                {toggling ? <CircularProgress size={18} /> : isActive ? 'Deactivate' : 'Reactivate'}
              </Button>
            )}
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {[`ID: ${id}`, partnerDomain, partnerType].filter(Boolean).join(' · ')}
          </Typography>
        </Container>
      </Box>

      {/* Tabs + Content */}
      <Box sx={{ bgcolor: 'background.paper', flex: 1 }}>
        <Container maxWidth="xl" sx={{ py: 0 }}>
          {/* Sub-nav tabs — mirrors site-level nav pattern */}
          {partner ? (
            <>
              <Box
                sx={{ position: 'relative', borderBottom: 1, borderColor: 'divider', mt: '10px' }}
              >
                <Box sx={{ display: 'flex' }}>
                  {(['Details', 'Users', 'Settings', 'Activity'] as const).map((label, i) => {
                    const isTabActive = i === tabIndex;
                    return (
                      <Box
                        key={label}
                        onClick={() => setTabIndex(i)}
                        sx={{
                          width: SUB_TAB_WIDTH,
                          textAlign: 'center',
                          pt: 0.5,
                          pb: 0.75,
                          cursor: 'pointer',
                          '&:hover .sub-tab-label': {
                            color: '#250e62',
                          },
                        }}
                      >
                        <Typography
                          className="sub-tab-label"
                          sx={{
                            fontSize: 16,
                            fontWeight: isTabActive ? 600 : 400,
                            color: isTabActive ? '#250e62' : '#6D6E71',
                            transition: 'color 0.2s ease',
                            userSelect: 'none',
                          }}
                        >
                          {label}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
                {/* Sliding underline indicator */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: tabIndex * SUB_TAB_WIDTH,
                    width: SUB_TAB_WIDTH,
                    height: 2,
                    bgcolor: '#250e62',
                    transition:
                      'left 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                />
              </Box>

              <Box sx={{ py: 3 }}>
                {tabIndex === 0 && (
                  <DetailsTab
                    partner={partner}
                    onUpdate={handlePartnerUpdate}
                    client={partnersClient}
                    isAdmin={isAdmin}
                  />
                )}
                {tabIndex === 1 && (
                  <UsersTab
                    partnerId={partner.partner_id}
                    client={partnersClient}
                    isAdmin={canEditPartner}
                  />
                )}
                {tabIndex === 2 && (
                  <SettingsTab
                    partnerId={partner.partner_id}
                    client={partnersClient}
                    isAdmin={isAdmin}
                    canEdit={canEditPartner}
                  />
                )}
                {tabIndex === 3 && (
                  <ActivityTab partnerId={partner.partner_id} client={partnersClient} />
                )}
              </Box>
            </>
          ) : program ? (
            <Box sx={{ py: 3 }}>
              <Stack spacing={2.5} sx={{ maxWidth: 600 }}>
                <TextField
                  label="Partner Name"
                  fullWidth
                  value={program.name}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Account Type"
                  fullWidth
                  value={program.partner_account_type || ''}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Partnership Tier"
                  fullWidth
                  value={program.partnership_tier || ''}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Status"
                  fullWidth
                  value={program.active ? 'Active' : 'Inactive'}
                  InputProps={{ readOnly: true }}
                />
                <PortalLaunchDateSection partnerId={String(id)} isAdmin={isAdmin} />
              </Stack>
            </Box>
          ) : null}
        </Container>
      </Box>
    </>
  );
}
